/**
 * Import d'un export **Password XP** CSV (« Fichier > Exporter > CSV » de
 * Password XP : ses utilisateurs doivent pouvoir partir avec TOUT leur
 * coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. L'export est separe par `;`, son en-tete est dans la langue
 * de l'outil (anglais, allemand ou neerlandais) :
 * `Title;User name;Account;URL;Password;Modified;Created;Expire on;Description;Modified by`.
 * Une ligne `>>>` annonce les fiches sans dossier ; une ligne `[Dossier]` (ou
 * `[A\B]` pour un sous-dossier) annonce les fiches du dossier qui suivent
 * (lu comme le lit Bitwarden, `passwordxp-csv-importer.ts`).
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvFreeColumnField, folderPathOf, parseCsvLine, splitCsvRecords, type ImportedEntry } from './csv-import.js';
import { serverHost } from './typed-fields.js';

type PasswordXpRole = 'title' | 'username' | 'account' | 'url' | 'password' | 'modified' | 'created' | 'expireOn' | 'description' | 'modifiedBy';

/** En-tetes de Password XP, anglais, allemands et neerlandais (ceux que Bitwarden traduit), compares sans casse. */
const PASSWORDXP_HEADERS: Readonly<Record<string, PasswordXpRole>> = {
  title: 'title',
  titel: 'title',
  'user name': 'username',
  username: 'username',
  benutzername: 'username',
  gebruikersnaam: 'username',
  account: 'account',
  konto: 'account',
  url: 'url',
  password: 'password',
  passwort: 'password',
  wachtwoord: 'password',
  modified: 'modified',
  'geändert am': 'modified',
  gewijzigd: 'modified',
  created: 'created',
  'erstellt am': 'created',
  gemaakt: 'created',
  'expire on': 'expireOn',
  'läuft ab am': 'expireOn',
  'verloopt op': 'expireOn',
  description: 'description',
  beschreibung: 'description',
  beschrijving: 'description',
  'modified by': 'modifiedBy',
  'geändert von': 'modifiedBy',
  'gewijzigd door': 'modifiedBy',
};

/** Colonnes reprises par un role de l'entree ; toute autre colonne non vide devient un champ. */
const PASSWORDXP_CONSUMED: ReadonlySet<PasswordXpRole> = new Set(['title', 'username', 'url', 'password', 'description']);

/** Ligne qui annonce les fiches sans dossier. */
const NO_FOLDER_MARK = '>>>';

/**
 * Horodatage Password XP `J-M-AAAA hh:mm:ss` (neerlandais) ou `J.M.AAAA` (allemand).
 * Un `/` n'est pas lu : `3/4/2024` est le 3 avril OU le 4 mars selon la langue.
 */
const PASSWORDXP_DATETIME = /^(\d{1,2})([-.])(\d{1,2})\2(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/u;

interface PasswordXpTable {
  readonly header: readonly string[];
  readonly roles: readonly (PasswordXpRole | null)[];
  readonly delimiter: string;
  readonly records: readonly string[];
}

/** Separateur de l'export : `;` (celui de Password XP) des que l'en-tete en porte un, sinon `,`. */
function delimiterOf(headerLine: string): string {
  return headerLine.includes(';') ? ';' : ',';
}

/** L'export decoupe et ses roles, ou `null` si le texte n'est pas un export Password XP. */
function passwordXpTable(text: string): PasswordXpTable | null {
  const records = splitCsvRecords(text);
  if (records.length === 0) return null;
  const delimiter = delimiterOf(records[0]!);
  const header = parseCsvLine(records[0]!, delimiter).map((label) => label.trim());
  const roles = header.map((label) => PASSWORDXP_HEADERS[label.toLowerCase()] ?? null);
  if (!roles.includes('title') || !roles.includes('password')) return null;
  // `Title` et `Password` ne suffisent pas (1Password, Passpack...) : il faut une colonne ou la ligne `>>>` propres a l'outil.
  const ownColumn = roles.includes('expireOn') || roles.includes('modifiedBy');
  const ownMark = records.slice(1).some((record) => parseCsvLine(record, delimiter)[0]?.trim() === NO_FOLDER_MARK);
  return ownColumn || ownMark ? { header, roles, delimiter, records: records.slice(1) } : null;
}

/** Un export Password XP se reconnait a son en-tete (`Title` / `Titel`, mot de passe) et a ce qui n'appartient qu'a lui. */
export function isPasswordXpCsv(text: string): boolean {
  return passwordXpTable(text) !== null;
}

/** Date civile exacte d'un horodatage Password XP (heure locale sans fuseau, lue en UTC), ou `null`. */
function passwordXpDate(raw: string): Date | null {
  const match = PASSWORDXP_DATETIME.exec(raw.trim());
  if (match === null) return null;
  const [day, , month, year, hour, minute, second] = match.slice(1).map((part) => Number(part ?? 0));
  const at = new Date(Date.UTC(year!, month! - 1, day!, hour!, minute!, second!));
  // Un jour ou un mois hors bornes (31-2, 13e mois) deborde sur la date suivante : illisible.
  return at.getUTCDate() === day && at.getUTCMonth() === month! - 1 && at.getTime() > 0 ? at : null;
}

/** La ligne `[Dossier]` d'un export : son chemin (`\` ou `/` separe les niveaux), ou `null` si la ligne est une fiche. */
function folderMark(values: readonly string[]): readonly string[] | null {
  const title = (values[0] ?? '').trim();
  if (!title.startsWith('[') || !title.endsWith(']') || title.length < 2) return null;
  // Une fiche nommee `[Travail]` porte au moins une autre valeur ; une ligne de dossier n'a que son titre.
  if (values.slice(1).some((value) => value.trim().length > 0)) return null;
  return folderPathOf(title.slice(1, -1)).folderPath ?? [];
}

/**
 * Parse un export Password XP : une ligne = une fiche, rangee dans le dossier
 * annonce par la derniere ligne `[Dossier]`. Au-dela de Bitwarden (qui garde
 * le dernier dossier apres `>>>`, prend toute fiche `[...]` pour un dossier
 * et garde les dates en texte) : `>>>` ramene a la racine ; une fiche nommee
 * `[...]` qui porte des valeurs reste une fiche ; `Modified` date le mot de
 * passe et `Expire on` devient son echeance (Sante du coffre), une date
 * illisible restant un champ ; nom de repli = hote puis identifiant ; tout
 * autre champ garde, masque sous un libelle de secret ; mot de passe jamais
 * rogne.
 */
export function parsePasswordXpCsv(text: string): ImportedEntry[] {
  const table = passwordXpTable(text);
  if (table === null) throw new Error('export Password XP : en-tete inattendu');
  const { header, roles, delimiter } = table;
  const entries: ImportedEntry[] = [];
  let folderPath: readonly string[] = [];
  for (const record of table.records) {
    const values = parseCsvLine(record, delimiter);
    if ((values[0] ?? '').trim() === NO_FOLDER_MARK) {
      folderPath = [];
      continue;
    }
    const folder = folderMark(values);
    if (folder !== null) {
      folderPath = folder;
      continue;
    }
    const raw = (role: PasswordXpRole): string => values[roles.indexOf(role)] ?? '';
    const get = (role: PasswordXpRole): string => raw(role).trim();
    const username = get('username');
    // Jamais rogne : une espace peut faire partie du mot de passe.
    const password = roles.includes('password') ? raw('password') : '';
    const url = get('url');
    const uris = url.length > 0 ? [url] : [];
    const isLogin = username.length > 0 || password.length > 0 || uris.length > 0;
    const changedAt = isLogin && password.length > 0 ? passwordXpDate(get('modified')) : null;
    const expiresOn = isLogin && password.length > 0 ? passwordXpDate(get('expireOn')) : null;
    const customFields: EntryCustomField[] = [];
    header.forEach((label, index) => {
      const role = roles[index] ?? null;
      if (role !== null && PASSWORDXP_CONSUMED.has(role)) return;
      if ((role === 'modified' && changedAt !== null) || (role === 'expireOn' && expiresOn !== null)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const title = get('title');
    const notes = get('description');
    if (title.length === 0 && !isLogin && notes.length === 0 && customFields.length === 0) continue;
    const host = uris.length > 0 ? serverHost(url).replace(/^www\./u, '') : '';
    entries.push({
      type: isLogin ? 'login' : 'note',
      name: title || host || url || username || 'Sans nom',
      username,
      password,
      uris,
      notes,
      totpSecret: '',
      favorite: false,
      ...(changedAt !== null ? { passwordChangedAt: changedAt.getTime() } : {}),
      ...(expiresOn !== null ? { passwordExpiry: expiresOn.toISOString().slice(0, 10) } : {}),
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(folderPath.length > 0 ? { folderPath } : {}),
    });
  }
  return entries;
}
